import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';

const AboutPage = () => {
  return (
    <div className="min-h-screen relative">
      {/* Mystical Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/9910db4e977c4892bacf08542838a807/3db5ea07c439b2c586f97cc1db92db72.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.15
        }}
      />

      {/* Gradient Overlay for better text readability */}
      <div 
        className="fixed inset-0 z-0 bg-gradient-to-b from-purple-900/40 via-slate-950/60 to-purple-900/40"
      />

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="max-w-6xl mx-auto px-6">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white drop-shadow-lg mb-6">
                About OhMyGAWD
              </h1>
              <p className="text-white/90 drop-shadow text-xl max-w-3xl mx-auto leading-relaxed">
                Guidance, Awareness, Wisdom, and Direction through the fusion of ancient wisdom and artificial intelligence
              </p>
            </motion.div>

            {/* Creator Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16"
            >
              <div className="order-2 lg:order-1">
                <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-sm">
                  <h2 className="font-serif text-4xl font-bold text-purple-900 mb-6">
                    Meet the Creator
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                    For over three decades, I've walked the path of spiritual exploration, learning from masters, teachers, gurus, coaches, and mentors across diverse traditions and methodologies. Each encounter, each lesson, each moment of awakening has contributed to a vast reservoir of experiential wisdom.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    From ancient Eastern philosophies to modern coaching techniques, from mystical traditions to practical psychology, I've absorbed and integrated knowledge that spans cultures, centuries, and consciousness levels. This journey wasn't just academic—it was lived, breathed, and embodied through years of practice, study, and deep inner work.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    OhMyGAWD represents the culmination of this lifetime of learning, now amplified through the boundless capacity of artificial intelligence to synthesize, analyze, and deliver personalized guidance at a scale previously impossible.
                  </p>
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-3xl p-1">
                    <div className="bg-white rounded-3xl p-8">
                      <div className="text-center">
                        <div 
                          className="w-48 h-48 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200"
                          style={{
                            backgroundImage: `url('https://static.readdy.ai/image/9910db4e977c4892bacf08542838a807/4452bdb2444609ea81db05097e08d5fc.jpeg')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                        <h3 className="font-serif text-2xl font-bold text-purple-900 mb-2">
                          The Wisdom Keeper
                        </h3>
                        <p className="text-purple-600 mb-4">
                          Decades of spiritual exploration and learning
                        </p>
                        <div className="flex justify-center gap-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-purple-900">30+</div>
                            <div className="text-sm text-gray-600">Years of Study</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold text-purple-900">20+</div>
                            <div className="text-sm text-gray-600">Teachers & Mentors</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold text-purple-900">∞</div>
                            <div className="text-sm text-gray-600">Wisdom Traditions</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* CyberAlchemy Foundation Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-16"
            >
              <div className="bg-gradient-to-br from-cyan-600 to-cyan-800 rounded-3xl p-1">
                <div className="bg-white rounded-3xl p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-2xl">
                          <i className="ri-flask-line text-3xl text-white"></i>
                        </div>
                        <h2 className="font-serif text-4xl font-bold text-cyan-900">
                          CyberAlchemy
                        </h2>
                      </div>
                      <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                        At the heart of OhMyGAWD's wisdom lies <strong>CyberAlchemy</strong>—a transformative methodology that bridges ancient alchemical principles with modern digital consciousness. This foundational framework represents decades of synthesis, combining timeless spiritual practices with cutting-edge understanding of how technology and consciousness intersect.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        CyberAlchemy is not just a philosophy—it's a practical system for personal transformation in the digital age. It teaches how to transmute challenges into growth, integrate shadow aspects, and navigate the complexities of modern life while maintaining spiritual alignment.
                      </p>
                      <a 
                        href="https://cyberalchemy.academy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white px-8 py-4 rounded-full font-semibold hover:from-cyan-700 hover:to-cyan-800 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                      >
                        Explore CyberAlchemy Academy
                        <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                    <div className="relative">
                      <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                        <img 
                          src="https://public.readdy.ai/ai/img_res/95c8d92b766f56e45d45f4575e54c078.jpg" 
                          alt="CyberAlchemy - Ancient Wisdom Meets Digital Consciousness" 
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="absolute -bottom-4 -right-4 bg-cyan-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                        <div className="text-sm font-semibold">Foundational Knowledge</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Vision Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 mb-16 shadow-sm"
            >
              <h2 className="font-serif text-4xl font-bold text-purple-900 mb-6 text-center">
                The Vision Behind OhMyGAWD
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-2xl text-purple-800 mb-4">
                    <i className="ri-lightbulb-line text-purple-600 mr-3"></i>
                    The Inspiration
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Throughout history, seekers have traveled great distances and waited years to receive wisdom from enlightened masters. Today, we have the technology to make profound guidance instantly accessible while preserving the depth and personalization that makes spiritual counsel truly transformative.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    OhMyGAWD bridges the gap between ancient wisdom and modern accessibility, ensuring that everyone can receive the guidance they need, when they need it, tailored to their unique circumstances and spiritual path.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-2xl text-purple-800 mb-4">
                    <i className="ri-magic-line text-purple-600 mr-3"></i>
                    The Technology
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    By combining decades of accumulated wisdom with advanced AI capabilities, OhMyGAWD can analyze patterns, synthesize insights, and deliver personalized guidance that draws from a vast knowledge base of spiritual teachings, psychological principles, and practical wisdom.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    The AI doesn't replace human wisdom—it amplifies it, making it possible to offer the depth of understanding typically reserved for one-on-one sessions with master teachers, available to anyone, anywhere, at any time.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Wisdom Sources */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-16"
            >
              <h2 className="font-serif text-4xl font-bold text-white drop-shadow-lg mb-12 text-center">
                Sources of Wisdom
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-sun-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-purple-900 mb-3">Ancient Traditions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Eastern philosophies, mystical traditions, indigenous wisdom, and timeless spiritual practices from cultures around the world.
                  </p>
                </div>

                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-brain-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-purple-900 mb-3">Modern Psychology</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Cognitive behavioral therapy, positive psychology, mindfulness-based interventions, and evidence-based coaching methodologies.
                  </p>
                </div>

                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-user-heart-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-purple-900 mb-3">Life Coaching</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Practical strategies for personal development, goal achievement, relationship building, and life transformation.
                  </p>
                </div>

                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-heart-3-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-purple-900 mb-3">Experiential Learning</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Decades of personal practice, direct experience, and lessons learned from challenges, breakthroughs, and transformations.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* The Difference Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-purple-800 to-purple-900 rounded-3xl p-8 mb-16 text-white"
            >
              <h2 className="font-serif text-4xl font-bold mb-6 text-center">
                What Makes OhMyGAWD Different
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-3xl mx-auto mb-4">
                    <i className="ri-book-open-line text-4xl text-cyan-400"></i>
                  </div>
                  <h3 className="font-serif text-2xl font-bold mb-4">Depth & Breadth</h3>
                  <p className="text-purple-100 leading-relaxed">
                    Not just surface-level advice, but profound insights drawn from a vast synthesis of wisdom traditions and practical experience.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-3xl mx-auto mb-4">
                    <i className="ri-user-settings-line text-4xl text-cyan-400"></i>
                  </div>
                  <h3 className="font-serif text-2xl font-bold mb-4">Personalization</h3>
                  <p className="text-purple-100 leading-relaxed">
                    Every response is tailored to your unique situation, considering context, background, and individual needs for truly relevant guidance.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-3xl mx-auto mb-4">
                    <i className="ri-time-line text-4xl text-cyan-400"></i>
                  </div>
                  <h3 className="font-serif text-2xl font-bold mb-4">Instant Access</h3>
                  <p className="text-purple-100 leading-relaxed">
                    No waiting for appointments or traveling to distant teachers. Profound wisdom is available whenever inspiration strikes or crisis emerges.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Mission Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/95 backdrop-blur-sm rounded-3xl p-12 text-center shadow-sm"
            >
              <div className="max-w-4xl mx-auto">
                <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-purple-600 to-purple-800 rounded-3xl mx-auto mb-8">
                  <i className="ri-compass-3-line text-4xl text-white"></i>
                </div>
                <h2 className="font-serif text-4xl font-bold text-purple-900 mb-6">
                  Our Mission
                </h2>
                <p className="text-gray-700 text-xl leading-relaxed mb-8">
                  To democratize access to profound wisdom and guidance, ensuring that every seeker has the opportunity to receive personalized spiritual counsel that can illuminate their path, resolve their challenges, and accelerate their growth.
                </p>
                <p className="text-purple-600 text-lg font-medium">
                  Because everyone deserves access to the wisdom that can transform their life—regardless of location, resources, or circumstances.
                </p>
              </div>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default AboutPage;